import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { type Signal, signal } from "@preact/signals";
import { sounds } from "../sound.ts";
import { KittenFace } from "./kneading.tsx";
import styles from "./news.module.css";

/** What a delivery opened, a line for each, as the kitten says it. */
export interface Opened {
  /** Challenges cleared, letters arrived and clays opened: にゃーん! */
  stages: string[];
  /** Materials to finish free pieces in: ごろごろ… */
  finishes: string[];
}

/** What the kitten tells the player once a delivery has opened something new. */
export interface News {
  /** Tells what was `opened`: resolves once the player has read it. */
  tell(opened: Opened): Promise<void>;
  /** The way back, as わかった goes (null while nothing is told). */
  readonly wayBack: (() => void) | null;
}

interface Told {
  opened: Opened;
  done(): void;
}

/** How long the kitten's にゃーん lasts before it purrs, when it does both (ms). */
const PURR_AFTER = 900;

/** The kitten's news, over the delivery slip. */
export function mountNews(root: HTMLElement): News {
  const told = signal<Told | null>(null);
  render(<NewsCard told={told} />, root);
  return {
    tell: (opened) =>
      new Promise((done) => {
        told.value = {
          opened,
          done() {
            told.value = null;
            done();
          },
        };
      }),
    get wayBack() {
      return told.value?.done ?? null;
    },
  };
}

function NewsCard({ told }: { told: Signal<Told | null> }) {
  const t = told.value;
  // Each piece of news starts afresh, the kitten's voice with it.
  return t && <Card key={t} told={t} />;
}

function Card({ told: { opened, done } }: { told: Told }) {
  // にゃーん! for new stages as it comes up, ごろごろ… for new finishes (after it, if both).
  const [purring, setPurring] = useState(!opened.stages.length);
  useEffect(() => {
    const purr = () => {
      setPurring(true);
      sounds.kitten("purr");
    };
    if (!opened.stages.length) return purr();
    sounds.kitten("meow");
    if (!opened.finishes.length) return;
    const later = setTimeout(purr, PURR_AFTER);
    return () => clearTimeout(later);
  }, []);
  return (
    <div class={styles.news}>
      <div class={styles.card} role="alertdialog" aria-modal="true" aria-label="お知らせ">
        <div class={styles.head}>
          <KittenFace class={`${styles.kitten} ${purring ? styles.purring : ""}`} />
          <p class={styles.says} key={String(purring)}>{purring ? "ごろごろ…" : "にゃーん！"}</p>
        </div>
        <ul class={styles.lines}>
          {[...opened.stages, ...opened.finishes].map((line) => <li key={line}>{line}</li>)}
        </ul>
        <button type="button" class="submit" onClick={done}>わかった</button>
      </div>
    </div>
  );
}
